import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowLeft, Edit, User, ChevronDown, ChevronUp, Phone, Mail, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderPatientView = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPersonalInfo, setShowPersonalInfo] = useState(false);

  const calculateAge = (birthdate) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    fetchPatient();
  }, [patientId]);

  const fetchPatient = async () => {
    try {
      const response = await axios.get(`${API}/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Remove provider notes for patient view
      const patientData = { ...response.data };
      if (patientData.health_record && patientData.health_record.provider_notes) {
        delete patientData.health_record.provider_notes;
      }
      setPatient(patientData);
    } catch (error) {
      // console.error('Error fetching patient:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMembershipBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="badge-active">Active</Badge>;
      case 'inactive':
        return <Badge className="badge-inactive">Inactive</Badge>;
      case 'expiring_soon':
        return <Badge className="badge-expiring">Expiring Soon</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getMembershipWarning = (status) => {
    if (status === 'expiring_soon') {
      return (
        <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm text-orange-800 font-medium">⚠️ Membership will expire soon.</p>
        </div>
      );
    }
    if (status === 'inactive') {
      return (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800 font-medium">❌ Membership has expired.</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading patient view...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Patient not found</p>
        </div>
      </div>
    );
  }

  // This component now renders the EXACT same view as the patient sees
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Provider Banner */}
      <div className="bg-cyan-700 text-white py-3 px-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="font-medium">👁️ Patient View - This is exactly what the patient sees</p>
          <div className="flex gap-3">
            <Link to="/provider/patients">
              <Button className="bg-white text-cyan-700 hover:bg-cyan-50" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back to My Patients
              </Button>
            </Link>
            <Link to={`/provider/patient/${patientId}/edit`}>
              <Button className="bg-white text-cyan-700 hover:bg-cyan-50" size="sm">
                <Edit size={16} className="mr-2" />
                Update Patient Record
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Exact Patient Dashboard View */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Personal Info Button - Top Right */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowPersonalInfo(!showPersonalInfo)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-300 rounded-lg hover:border-cyan-400 hover:bg-cyan-50 transition-all shadow-sm text-sm"
            title="View personal information"
          >
            <User size={16} className="text-cyan-700" />
            <span className="font-medium text-slate-700">Personal Info</span>
            {showPersonalInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>

        {/* Collapsible Personal Information Section */}
        {showPersonalInfo && (
          <div className="mb-6 animate-fadeIn">
            <div className="card border-2 border-cyan-200 bg-gradient-to-br from-white to-cyan-50">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900">Your Personal Information</h2>
                <button
                  onClick={() => setShowPersonalInfo(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <ChevronUp size={20} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="flex justify-center md:col-span-1">
                  {patient.identifying_data.profile_photo ? (
                    <img
                      src={patient.identifying_data.profile_photo}
                      alt={patient.identifying_data.full_name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-cyan-200 shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 border-4 border-white shadow-lg flex items-center justify-center">
                      <span className="text-3xl font-bold text-white">
                        {patient.identifying_data.full_name?.charAt(0) || '?'}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="md:col-span-3 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Full Name</p>
                    <p className="text-slate-900 font-medium">{patient.identifying_data.full_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Birthdate</p>
                    <p className="text-slate-900">{patient.identifying_data.birthdate}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Age</p>
                    <p className="text-slate-900">{calculateAge(patient.identifying_data.birthdate)} years</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Sex Assigned at Birth</p>
                    <p className="text-slate-900">{patient.identifying_data.sex}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Pronouns</p>
                    <p className="text-slate-900">{patient.identifying_data.pronouns || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Birthplace</p>
                    <p className="text-slate-900">{patient.identifying_data.birthplace}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Home Address</p>
                    <p className="text-slate-900">{patient.identifying_data.home_address}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Occupation</p>
                    <p className="text-slate-900">{patient.identifying_data.occupation}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Employer</p>
                    <p className="text-slate-900">{patient.identifying_data.employer_name || patient.identifying_data.employer || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Emergency Contact</p>
                    <p className="text-slate-900 font-medium">{patient.identifying_data.emergency_contact?.name || patient.identifying_data.emergency_contact_name || 'Not provided'}</p>
                    <p className="text-slate-600 text-sm">{patient.identifying_data.emergency_contact?.relationship || patient.identifying_data.emergency_contact_relationship || ''}</p>
                    <p className="text-slate-600 text-sm">{patient.identifying_data.emergency_contact?.phone || patient.identifying_data.emergency_contact_phone || ''}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Greeting Section */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute -left-4 top-0 w-1 h-16 bg-gradient-to-b from-cyan-500 to-blue-500 rounded-full"></div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-2">
              Hi, {patient.identifying_data.nickname}! 👋
            </h1>
            <p className="text-lg text-slate-600">Welcome back to your health dashboard.</p>
          </div>
        </div>

        {/* Membership Status and Care Team - 2 Column Layout */}
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Membership Status */}
          <div>
            <div className="card border-2 border-cyan-200 bg-gradient-to-br from-white to-cyan-50 shadow-lg hover:shadow-xl transition-shadow h-full">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b-2 border-cyan-100">Membership Status</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Membership</span>
                  {getMembershipBadge(patient.membership_status.status)}
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Start Date</p>
                  <p className="text-slate-900">{patient.membership_status.start_date}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Expiry Date</p>
                  <p className="text-slate-900">{patient.membership_status.expiry_date}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Onboarding</p>
                  <p className="text-slate-900 capitalize">{patient.membership_status.onboarding_status}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Comprehensive Health Interview</p>
                  <p className="text-slate-900 capitalize">{patient.membership_status.health_interview_status}</p>
                </div>
                {patient.membership_status.health_interview_date && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">First Patient Encounter</p>
                    <p className="text-slate-900">{patient.membership_status.health_interview_date}</p>
                  </div>
                )}
              </div>
              {getMembershipWarning(patient.membership_status.status)}
            </div>
          </div>

          {/* Care Team */}
          <div>
            <div className="card border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50 shadow-lg hover:shadow-xl transition-shadow h-full">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b-2 border-blue-100">Your Care Team</h2>
              
              <div className="space-y-4">
                {patient.care_team
                  .sort((a, b) => {
                    if (a.user_type === 'external' && b.user_type !== 'external') return 1;
                    if (a.user_type !== 'external' && b.user_type === 'external') return -1;
                    return 0;
                  })
                  .map((member) => (
                    <div key={member.id} className="flex items-start space-x-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
                      {member.user_type && member.user_type !== 'external' && (
                        <div className="flex-shrink-0">
                          {member.avatar ? (
                            <img
                              src={member.avatar}
                              alt={member.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-cyan-200"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center border-2 border-white shadow">
                              <span className="text-lg font-bold text-white">
                                {member.name?.charAt(0) || '?'}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{member.name}</h3>
                          {member.user_type === 'external' && (
                            <span className="px-2 py-0.5 text-xs bg-slate-200 text-slate-600 rounded-full">External</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{member.role}</p>
                        {member.clinic_hours && (
                          <p className="text-xs text-slate-500 mt-1">{member.clinic_hours}</p>
                        )}
                        
                        {member.user_type && member.user_type !== 'external' && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {member.phone && (
                              <a
                                href={`tel:${member.phone}`}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-cyan-700 bg-cyan-50 rounded-full hover:bg-cyan-100 transition-colors"
                              >
                                <Phone size={12} className="mr-1" />
                                Call
                              </a>
                            )}
                            {member.phone && (
                              <a
                                href={`sms:${member.phone}`}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-cyan-700 bg-cyan-50 rounded-full hover:bg-cyan-100 transition-colors"
                              >
                                <MessageCircle size={12} className="mr-1" />
                                SMS
                              </a>
                            )}
                            {member.email && (
                              <a
                                href={`mailto:${member.email}`}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-cyan-700 bg-cyan-50 rounded-full hover:bg-cyan-100 transition-colors"
                              >
                                <Mail size={12} className="mr-1" />
                                Email
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Health Goals and Problem List - 2 Column Layout */}
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Health Goals */}
          <div className="card border-2 border-green-200 bg-gradient-to-br from-white to-green-50 shadow-lg hover:shadow-xl transition-shadow h-full">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b-2 border-green-100">Your Health Goals</h2>
            
            {patient.health_goals.length === 0 ? (
              <p className="text-slate-500 text-sm">No health goals set yet.</p>
            ) : (
              <div className="space-y-4">
                {patient.health_goals.map((goal) => (
                  <div key={goal.id} className="p-4 bg-gradient-to-r from-cyan-50 to-sky-50 rounded-lg border border-cyan-100">
                    <div className="flex items-start justify-between mb-2">
                      <span className="inline-block px-3 py-1 text-xs font-semibold text-cyan-700 bg-white rounded-full border border-cyan-200">
                        {goal.category}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{goal.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Problem List */}
          <div className="card border-2 border-rose-200 bg-gradient-to-br from-white to-rose-50 shadow-lg hover:shadow-xl transition-shadow h-full">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b-2 border-rose-100">Problem List</h2>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                {patient.health_record.problem_list || 'No problems listed yet.'}
              </p>
            </div>
          </div>
        </div>

        {/* Health Record (Accordion) - Full Width */}
        <div className="mb-6">
          <div className="card border-2 border-purple-200 bg-gradient-to-br from-white to-purple-50 shadow-lg hover:shadow-xl transition-shadow">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b-2 border-purple-100">Your Health Record</h2>
            <div className="space-y-6">
              {/* Clinical History Section */}
              <div className="p-5 rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white shadow-sm">
                <h3 className="text-lg font-semibold text-indigo-900 mb-4 pb-2 border-b-2 border-indigo-200 flex items-center gap-2">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                  Clinical History
                </h3>
                <Accordion type="single" collapsible className="w-full">
                  {[
                    { key: 'birth_history', label: 'Birth History' },
                    { key: 'immunization_history', label: 'Immunization History' },
                    { key: 'past_medical_history', label: 'Past Medical History' },
                    { key: 'past_surgical_history', label: 'Past Surgical History' },
                    { key: 'obgyne_menstrual_history', label: 'OB-Gyne & Menstrual History' },
                    { key: 'sexual_history', label: 'Sexual History' },
                    { key: 'medications', label: 'Medication History' },
                    { key: 'allergies', label: 'Known Allergies' }
                  ].map(section => (
                    <AccordionItem key={section.key} value={section.key}>
                      <AccordionTrigger>{section.label}</AccordionTrigger>
                      <AccordionContent>
                        <div className="p-4 bg-white rounded-lg border border-indigo-100">
                          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                            {patient.health_record[section.key] || 'No information available yet.'}
                          </p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Lifestyle Summary Section */}
              <div className="p-5 rounded-xl border-2 border-orange-200 bg-gradient-to-br from-orange-50/50 to-white shadow-sm">
                <h3 className="text-lg font-semibold text-orange-900 mb-4 pb-2 border-b-2 border-orange-200 flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                  Lifestyle Summary
                </h3>
                <Accordion type="single" collapsible className="w-full">
                  {[
                    { key: 'nutrition', label: 'Nutrition' },
                    { key: 'physical_activity', label: 'Physical Activity' },
                    { key: 'sleep', label: 'Sleep' },
                    { key: 'substance_use', label: 'Substance Use' },
                    { key: 'relationships', label: 'Relationships' },
                    { key: 'stressors', label: 'Stressors' },
                    { key: 'resiliency_practices', label: 'Resiliency Practices' }
                  ].map(section => (
                    <AccordionItem key={section.key} value={section.key}>
                      <AccordionTrigger>{section.label}</AccordionTrigger>
                      <AccordionContent>
                        <div className="p-4 bg-white rounded-lg border border-orange-100">
                          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                            {patient.health_record[section.key] || 'No information available yet.'}
                          </p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderPatientView;
